/**
 * UIIcon - Efficient SVG icon component using IconCache
 *
 * This component creates lightweight SVG icons by cloning cached SVG elements
 * instead of making HTTP requests for each instance.
 */

import { UIElement } from '@ui';
import iconCache from './IconCache.js';

class UIIcon extends UIElement {
  constructor(iconName, options = {}) {
    super(document.createElement('div'));

    const {
      color = 'currentColor',
      strokeColor = null,
      size = 16,
      className = '',
    } = options;

    this.iconName = iconName;
    this.options = { color, strokeColor, size };

    // Optional onload callback
    this.onload = null;

    // Set container size
    if (size) {
      this.setStyle('width', [`${size}px`]);
      this.setStyle('height', [`${size}px`]);
    }

    // Add any additional classes
    if (className) {
      this.setClass(className);
    }

    // Load and render the icon
    this._renderIcon();
  }

  /**
   * Update the icon with new options
   * @param {Object} newOptions - New options to apply
   */
  async updateIcon(newOptions = {}) {
    // Check if component has been destroyed
    if (!this.dom) return;

    this.options = { ...this.options, ...newOptions };

    // Update container size if changed
    if ('size' in newOptions && newOptions.size) {
      this.setStyle('width', [`${newOptions.size}px`]);
      this.setStyle('height', [`${newOptions.size}px`]);
    }

    await this._renderIcon();
  }

  /**
   * Change the icon to a different one
   * @param {string} newIconName - New icon name
   * @param {Object} newOptions - Optional new options
   */
  async changeIcon(newIconName, newOptions = {}) {
    // Check if component has been destroyed
    if (!this.dom) return;

    this.iconName = newIconName;
    if (Object.keys(newOptions).length > 0) {
      this.options = { ...this.options, ...newOptions };
    }
    await this._renderIcon();
  }

  /**
   * Set a callback to be called when the SVG is loaded and rendered
   * @param {Function} callback
   */
  onLoad(callback) {
    this.loadCallback = callback;
  }

  /**
   * Private method to render the icon
   * @private
   */
  async _renderIcon() {
    // Check if component has been destroyed
    if (!this.dom) return;

    try {
      const svgElement = await iconCache.createIcon(
        this.iconName,
        this.options,
      );

      // Double-check DOM still exists after async operation
      if (!this.dom) return;

      // Clear existing content and add new SVG
      this.dom.innerHTML = '';
      this.dom.appendChild(svgElement);

      // Call onload callback if set
      if (typeof this.loadCallback === 'function') {
        this.loadCallback(svgElement);
      }
    } catch (error) {
      console.error(`Error rendering icon ${this.iconName}:`, error);

      // Only add fallback if DOM still exists
      if (this.dom) {
        this.dom.innerHTML =
          '<div style="width: 100%; height: 100%; background: #ccc; display: flex; align-items: center; justify-content: center; font-size: 8px;">?</div>';
      }
    }
  }

  /**
   * Updates the fill color of the icon.
   * @param {string} newColor
   */
  async setColor(newColor) {
    if (!this.dom) return;
    if (this.options.color === newColor) return;
    this.options.color = newColor;
    await this._renderIcon();
  }

  /**
   * Updates the stroke color of the icon.
   * @param {string | null} newStrokeColor
   */
  async setStrokeColor(newStrokeColor) {
    if (!this.dom) return;
    if (this.options.strokeColor === newStrokeColor) return;
    this.options.strokeColor = newStrokeColor;
    await this._renderIcon();
  }

  /**
   * Cleanup method for UIIcon destruction
   */
  destroy() {
    // Clear references
    this.iconName = null;
    this.options = null;
    this.loadCallback = null;

    // Call parent destroy method for DOM cleanup
    super.destroy();
  }

  /**
   * Static method to create an icon element directly (for convenience)
   * @param {string} iconName - Name of the icon
   * @param {Object} options - Icon options
   * @returns {UIIcon} New UIIcon instance
   */
  static create(iconName, options = {}) {
    return new UIIcon(iconName, options);
  }
}

export default UIIcon;
